import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight, MapPin, Sprout } from "lucide-react";
import { scrollToId } from "./Header";
import { waLink, CONTACT, TAGLINE } from "@/lib/content";

const ease = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "Engenharia", c: "" },
  { t: "que entende", c: "italic text-[#8FC690]" },
  { t: "a terra.", c: "" },
];

const MaskLine = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Contours = () => (
  <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.18]" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
      <motion.path
        key={i}
        d={`M-50 ${520 - i * 55} C 150 ${430 - i * 60}, 300 ${640 - i * 50}, 480 ${500 - i * 58} S 760 ${380 - i * 52}, 860 ${460 - i * 55}`}
        fill="none"
        stroke="#8FC690"
        strokeWidth="1"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 2.6, delay: 0.6 + i * 0.12, ease }}
      />
    ))}
  </svg>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="grain relative min-h-[88svh] overflow-hidden bg-[#1B2F1D] text-[#F9F8F3] md:min-h-[100svh]"
    >
      <Contours />
      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 pb-6 pt-16 sm:px-8 md:pb-16 md:pt-32 lg:grid-cols-12 lg:gap-8 lg:pb-24">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease }}
            className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.14em] text-[#8FC690]"
          >
            <span className="h-px w-10 bg-[#8FC690]" /> Agronomia · Engenharia
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif text-[13vw] font-semibold leading-[0.95] tracking-[-0.035em] sm:text-[10vw] lg:text-[6.2rem] xl:text-[7rem]">
            {LINES.map((l, i) => (
              <MaskLine key={l.t} i={i} className={l.c}>
                {l.t}
              </MaskLine>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9, ease }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#F9F8F3]/75 md:text-lg"
          >
            {TAGLINE}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.95, ease }}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#8FC690]/40 bg-[#8FC690]/10 px-4 py-2 text-sm font-medium text-[#8FC690]"
          >
            <Sprout className="h-4 w-4" /> Soluções para o setor rural e urbano
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease }}
            className="mt-7 flex flex-col gap-3 sm:flex-row"
          >
            <button
              data-testid="hero-services-btn"
              onClick={() => scrollToId("servicos")}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-[#F9F8F3] px-7 py-4 text-sm font-semibold text-[#0F1F10] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#8FC690]"
            >
              Conheça os serviços
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-whatsapp-btn"
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center justify-center gap-3 rounded-full border border-[#F9F8F3]/30 px-7 py-4 text-sm font-semibold transition-[border-color,background-color] duration-300 hover:border-[#8FC690] hover:bg-white/5"
            >
              WhatsApp {CONTACT.phone}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            data-testid="hero-image-frame"
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.6, delay: 0.4, ease }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative aspect-[16/9] w-full overflow-hidden rounded-t-[999px] rounded-b-[28px] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.8)] sm:aspect-[5/5] lg:aspect-[4/5]"
          >
            <motion.img
              src="/img/soja-sunset.webp"
              alt="Lavoura de soja ao pôr do sol na Metade Sul do RS — foto real da Melo Agro Engenharia"
              width={960}
              height={1280}
              fetchPriority="high"
              style={{ y: imgY, scale: 1.2, objectPosition: "center 70%" }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[#233D25]/50" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B2F1D]/70 via-transparent to-transparent" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -30 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 1.2, ease }}
            className="absolute -left-4 -top-6 sm:-left-10"
          >
            <div className="spin-slow grid h-28 w-28 place-items-center rounded-full bg-[#5D995E] text-[#0F1F10]">
              <svg viewBox="0 0 100 100" className="absolute h-full w-full">
                <defs><path id="circ" d="M50 50 m-38 0 a38 38 0 1 1 76 0 a38 38 0 1 1 -76 0" /></defs>
                <text className="fill-[#0F1F10] font-mono text-[9.5px] uppercase tracking-[0.15em]">
                  <textPath href="#circ">Região Sul · Campo · Técnica ·</textPath>
                </text>
              </svg>
              <img src="/brand/icon-white.png" alt="" aria-hidden="true" className="h-10 w-10 object-contain" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
